import React from 'react';
import { Card, CardContent, Typography, IconButton, Box, Chip } from '@mui/material';
import { Star, StarBorder, TrendingUp, TrendingDown } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../../hooks/useFavorites';
import { Market } from '../../types';
import { formatPercent, formatCurrency } from '../../utils/formatters';

interface MarketCardProps {
  market: Market;
  showCountry?: boolean;
}

export const MarketCard: React.FC<MarketCardProps> = ({ market, showCountry = true }) => {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(market.id);

  const handleCardClick = () => {
    navigate(`/market/${market.id}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(market.id);
  };

  const getTrendIcon = (change: number | null | undefined) => {
    if (change === null || change === undefined) return null;
    return change > 0 ? (
      <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
    ) : (
      <TrendingDown sx={{ color: 'error.main', fontSize: 20 }} />
    );
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3,
        },
        '&:active': {
          transform: 'translateY(-2px)',
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
          <Box flex={1}>
            <Typography variant="h6" component="h2" gutterBottom>
              {market.name}
            </Typography>
            {showCountry && (
              <Typography variant="caption" color="text.secondary" display="block">
                {market.country} • {market.continent}
              </Typography>
            )}
            {market.data_period && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                Data as of {new Date(market.data_period).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleFavoriteClick}
            sx={{ color: favorite ? 'warning.main' : 'action.disabled' }}
          >
            {favorite ? <Star /> : <StarBorder />}
          </IconButton>
        </Box>

        {(market.current_occupancy !== null || market.current_adr !== null) && (
          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            {market.current_occupancy !== null && (
              <Chip
                label={`Occ: ${formatPercent(market.current_occupancy)}`}
                size="small"
                variant="outlined"
              />
            )}
            {market.current_adr !== null && (
              <Chip
                label={`ADR: ${formatCurrency(market.current_adr, 'USD', false)}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {market.current_revpar !== null && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              RevPAR:
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatCurrency(market.current_revpar, 'USD')}
            </Typography>
            {market.yoy_revpar_change !== null && market.yoy_revpar_change !== undefined && (
              <>
                {getTrendIcon(market.yoy_revpar_change)}
                <Typography
                  variant="body2"
                  color={
                    market.yoy_revpar_change > 0 ? 'success.main' : 'error.main'
                  }
                >
                  {formatPercent(market.yoy_revpar_change, true)}
                </Typography>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
